import { useState, useEffect } from "react";
import {
  Anchor,
  Banner,
  Button,
  DownloadSVG,
  RefreshSVG,
} from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  eventTypes,
  objectTypes,
  useTracker,
  withTrackEvent,
  WithTrackEventProps,
} from "@newsela/snitch";

import { DIRECTORY_CSV_GENERATION_STATUS } from "@/directory/types";

import {
  bannerBody,
  bannerCloseButtonCss,
  bannerCss,
  bannerCtaCss,
} from "./BannerToDisplayStatusOfDownloadRequest.style";

type AnchorProps = React.ComponentProps<typeof Anchor> | WithTrackEventProps;
const AnchorWithTrackEvent = withTrackEvent(
  Anchor,
) as React.ForwardRefExoticComponent<
  AnchorProps & React.RefAttributes<HTMLAnchorElement>
>;

export interface BannerToDisplayStatusOfDownloadRequestProps {
  directoryCsvGenerationStatus: DIRECTORY_CSV_GENERATION_STATUS | undefined;
  directoryType: string;
  downloadUrl: string;
  error: boolean;
  isVisible: boolean;
  setIsVisible: (arg: boolean) => void;
  onButtonClick: () => void;
  refetchingStatusApiResponse: boolean;
}

const ButtonWithTrackEvent = withTrackEvent(Button);

export const BannerToDisplayStatusOfDownloadRequest = ({
  refetchingStatusApiResponse,
  directoryCsvGenerationStatus,
  directoryType,
  downloadUrl,
  error,
  isVisible = false,
  setIsVisible,
  onButtonClick,
}: BannerToDisplayStatusOfDownloadRequestProps) => {
  const BANNER_VARIANTS = {
    [DIRECTORY_CSV_GENERATION_STATUS.COMPLETED]: {
      bannerType: Banner.types.success,
      bannerBodyText: `Your csv request has been generated and is ready to download.`,
      bannerButtonText: `Download ${directoryType} directory.csv`,
    },
    [DIRECTORY_CSV_GENERATION_STATUS.FAILED]: {
      bannerType: Banner.types.error,
      bannerBodyText:
        "There was an issue processing your request. Please try again.",
      bannerButtonText: null,
    },
    [DIRECTORY_CSV_GENERATION_STATUS.IN_PROGRESS]: {
      bannerType: Banner.types.info,
      bannerBodyText: `Request in progress. Generating the ${directoryType} directory .csv may take several minutes. You can return anytime to retrieve the file.`,
      bannerButtonText: "Check if download is ready",
    },
  };

  const { trackAction, getEventName } = useTracker();

  const sharedTrackActionProps = {
    event_name: getEventName(
      objectTypes.user,
      actionPrefixes.initiate,
      actionNames.cancel,
    ),
    event_type: eventTypes.click,
    dimension_properties: {
      description: `User closed download status banner`,
    },
  };

  const [buttonText, setButtonText] = useState(
    BANNER_VARIANTS[DIRECTORY_CSV_GENERATION_STATUS.IN_PROGRESS]
      .bannerButtonText,
  );

  useEffect(() => {
    if (refetchingStatusApiResponse) {
      setButtonText("Checking...");
    } else {
      setButtonText("Check if download is ready");
    }
  }, [refetchingStatusApiResponse]);

  const csvStatus =
    directoryCsvGenerationStatus as keyof typeof BANNER_VARIANTS;

  if (!isVisible) {
    return <></>;
  }

  if (error) {
    return (
      <Banner
        body={BANNER_VARIANTS.Failed?.bannerBodyText}
        bannerType={BANNER_VARIANTS.Failed?.bannerType}
        dataProps={{
          "data-qa-selector": `${BANNER_VARIANTS.Failed?.bannerType}-download-directory-banner`,
        }}
        isDismissible
        onClose={() => {
          trackAction({ ...sharedTrackActionProps });
          setIsVisible(false);
        }}
        __cssFor={{
          root: bannerCss,
          body: bannerBody,
        }}
      />
    );
  }
  return (
    <Banner
      body={BANNER_VARIANTS[csvStatus]?.bannerBodyText}
      bannerType={BANNER_VARIANTS?.[csvStatus]?.bannerType}
      dataProps={{
        "data-qa-selector": `${BANNER_VARIANTS[csvStatus]?.bannerType}-download-directory-banner`,
      }}
      isDismissible
      onClose={() => {
        trackAction({ ...sharedTrackActionProps });
        setIsVisible(false);
      }}
      __renderCtaSection={(spreadableProps) => {
        if (
          csvStatus &&
          csvStatus === DIRECTORY_CSV_GENERATION_STATUS.COMPLETED
        ) {
          return (
            <AnchorWithTrackEvent
              {...spreadableProps}
              actionName={actionNames.download}
              actionPrefix={actionPrefixes.initiate}
              dataProps={{
                "data-qa-selector": `download-completed-${directoryType}-directory-csv-file`,
              }}
              href={downloadUrl}
              icon={{
                isIconBeforeText: true,
                SvgComponent: DownloadSVG,
              }}
              objectType={objectTypes.report}
              streamProperties={{
                description: `Download completed ${directoryType} directory csv file.`,
              }}
              styleAnchorAsButton={{
                flavor: Button.flavors.muted,
                size: Button.sizes.sm,
              }}
              __cssFor={{
                root: bannerCtaCss,
              }}
            >
              {BANNER_VARIANTS[csvStatus]?.bannerButtonText || ""}
            </AnchorWithTrackEvent>
          );
        } else if (
          csvStatus &&
          csvStatus === DIRECTORY_CSV_GENERATION_STATUS.IN_PROGRESS
        ) {
          return (
            <ButtonWithTrackEvent
              {...spreadableProps}
              actionName={actionNames.interact}
              actionPrefix={actionPrefixes.initiate}
              dataProps={{
                "data-qa-selector": `check-if-${directoryType}-directory-csv-file-is-ready-to-download`,
              }}
              flavor={Button.flavors.muted}
              icon={{
                alignment: Button.iconAlignments.left,
                SvgComponent: RefreshSVG,
              }}
              objectType={objectTypes.report}
              onClick={() => {
                onButtonClick();
              }}
              size={Button.sizes.sm}
              streamProperties={{
                description: `Check if ${directoryType} directory csv file is ready to download.`,
              }}
              __cssFor={{
                root: bannerCtaCss,
              }}
            >
              {buttonText}
            </ButtonWithTrackEvent>
          );
        } else {
          return <div />;
        }
      }}
      __cssFor={{
        root: bannerCss,
        body: bannerBody,
        closeButton: bannerCloseButtonCss,
        cta: bannerCtaCss,
      }}
    />
  );
};

export default BannerToDisplayStatusOfDownloadRequest;
